
import React from 'react';
import { Table, Tag, Empty, Tooltip, Button } from 'antd';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { handleDownloadExcel } from '../../utils';

class LogAccountBalance extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
    }

    // Helper Method: แปลงรูปแบบตัวเลขสกุลเงิน
    formatCurrency = (amount) => {
        const numericAmount = Number(amount);
        if (amount === null || amount === undefined || amount === '' || isNaN(numericAmount)) {
            return "N/A";
        }
        return `${Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numericAmount)} THB`;
    };

    // Helper Method: แปลงรูปแบบวันที่
    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const locale = this.props.tFormat === 'en' ? 'en-US' : 'th-TH';
        return new Date(dateString).toLocaleString(locale);
    };

    // Helper Method: คำนวณยอดสุทธิ
    calculateNetBalance = (inactiveBalance, activeBalance) => {
        const inactive = Number(inactiveBalance);
        const active = Number(activeBalance);

        if (isNaN(inactive) || isNaN(active) || active === 0 || inactive === 0) {
            return { raw: 0, formatted: this.formatCurrency(0) };
        }

        const net = inactive - active;
        return { raw: net, formatted: this.formatCurrency(net) };
    };

    // Helper Method: คำนวณระยะเวลาที่เปิดใช้งาน
    formatDuration = (activatedAt, inactivatedAt) => {
        const t = this.props.t || ((key, fallback) => fallback || key);
        if (!activatedAt || !inactivatedAt) return 'N/A';

        const start = new Date(activatedAt).getTime();
        const end = new Date(inactivatedAt).getTime();
        if (isNaN(start) || isNaN(end) || end < start) return 'N/A';

        const diffInSeconds = Math.floor((end - start) / 1000);
        const days = Math.floor(diffInSeconds / 86400);
        const hours = Math.floor((diffInSeconds % 86400) / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        const parts = [];
        if (days > 0) parts.push(`${days} ${t('days', 'วัน')}`);
        if (hours > 0) parts.push(`${hours} ${t('ชม.', 'ชม.')}`);
        if (minutes > 0) parts.push(`${minutes} ${t('นาที', 'นาที')}`);
        if (seconds > 0 || parts.length === 0) parts.push(`${seconds} ${t('วินาที', 'วินาที')}`);

        return parts.join(' ');
    };

    render() {
        const { data = [], t, startDate, endDate, loading } = this.props;

        // จัดเตรียมข้อมูลสำหรับ Table และ Excel ในรอบเดียว
        const tableRows = data.map((record, index) => {
            const netInfo = this.calculateNetBalance(record.inactiveBalance, record.activeBalance);
            return {
                key: record.id || index,
                accountName: record.accountName || 'N/A',
                accountNumber: record.accountNumber || 'N/A',
                activeBalance: record.activeBalance,
                inactiveBalance: record.inactiveBalance,
                formattedActiveBalance: this.formatCurrency(record.activeBalance),
                formattedInactiveBalance: this.formatCurrency(record.inactiveBalance),
                netRaw: netInfo.raw,
                netBalance: netInfo.formatted,
                activatedAt: this.formatDateTime(record.activatedAt),
                inactivatedAt: this.formatDateTime(record.inactivatedAt),
                duration: this.formatDuration(record.activatedAt, record.inactivatedAt),
            };
        });

        const dataForExcel = tableRows.map((item) => ({
            [t?.('accountName') || 'ชื่อบัญชี']: item.accountName,
            [t?.('accountNumber') || 'เลขที่บัญชี']: item.accountNumber,
            [t?.('Remaining balance upon activation.') || 'ยอดเงินเมื่อเปิดใช้งาน']: item.formattedActiveBalance,
            [t?.('Remaining balance at deactivation.') || 'ยอดเงินเมื่อปิดใช้งาน']: item.formattedInactiveBalance,
            [t?.('Net amount') || 'ยอดสุทธิ']: item.netBalance,
            [t?.('Activation time') || 'เวลาเปิดใช้งาน']: item.activatedAt,
            [t?.('Deactivation time') || 'เวลาปิดใช้งาน']: item.inactivatedAt,
            [t?.('activeDuration') || 'ระยะเวลาที่เปิดใช้งาน']: item.duration,
        }));

        const columns = [
            {
                title: t ? t('accountName') : 'ชื่อบัญชี',
                dataIndex: 'accountName',
                key: 'accountName',
            },
            {
                title: t ? t('accountNumber') : 'เลขที่บัญชี',
                dataIndex: 'accountNumber',
                key: 'accountNumber',
            },
            {
                title: t ? t('Remaining balance upon activation.') : 'ยอดเงินเมื่อเปิดใช้งาน',
                dataIndex: 'formattedActiveBalance',
                key: 'activeBalance',
                align: 'right',
            },
            {
                title: t ? t('Remaining balance at deactivation.') : 'ยอดเงินเมื่อปิดใช้งาน',
                dataIndex: 'formattedInactiveBalance',
                key: 'inactiveBalance',
                align: 'right',
            },
            {
                title: (
                    <div>
                        {t ? t('Net amount') : 'ยอดสุทธิ'}{' '}
                        <Tooltip title={t ? t('Net amount is calculated as inactive balance minus active balance.') : 'ยอดสุทธิคำนวณจาก ยอดเงินเมื่อปิดใช้งาน ลบด้วย ยอดเงินเมื่อเปิดใช้งาน'}>
                            <span><QuestionCircleOutlined /></span>
                        </Tooltip>
                    </div>
                ),
                key: 'netBalance',
                align: 'right',
                render: (_, record) => {
                    if (record.netBalance === 'N/A') return 'N/A';
                    const color = record.netRaw > 0 ? 'green' : record.netRaw < 0 ? 'red' : 'default';
                    return <Tag color={color}>{record.netBalance}</Tag>;
                },
            },
            {
                title: t ? t('Activation time') : 'เวลาเปิดใช้งาน',
                dataIndex: 'activatedAt',
                key: 'activatedAt',
            },
            {
                title: t ? t('Deactivation time') : 'เวลาปิดใช้งาน',
                dataIndex: 'inactivatedAt',
                key: 'inactivatedAt',
            },
            {
                title: t ? t('activeDuration') : 'ระยะเวลาที่เปิดใช้งาน',
                dataIndex: 'duration',
                key: 'activeDuration',
            },
        ];

        const message = `Account_Balance_Log_${startDate ? new Date(startDate).toISOString().slice(0, 10) : 'start'}_to_${endDate ? new Date(endDate).toISOString().slice(0, 10) : 'end'}`;
        const title = `Account Balance Log Report`;

        let emptyData = !data || data.length === 0
        return (
            <div className="body table-responsive" style={{ padding: '16px' }}>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => emptyData ? alert("No data") : handleDownloadExcel(dataForExcel, message, title)}
                        disabled={emptyData}
                    >
                        {t ? t('Download Excel') : 'Download Excel'}
                    </Button>
                </div>
                <Table
                    className="col-lg-12"
                    columns={columns}
                    dataSource={tableRows}
                    loading={loading}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t ? t("No data") : "No data"} /> }}
                    pagination={{ pageSize: 100 }}
                    sticky
                    size="middle"
                    scroll={{ x: "max-content" }}
                />
            </div>
        );
    }
}

export default LogAccountBalance;